import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Topbar } from '@/components/Topbar';
import { SkipLink } from '@/components/SkipLink';
import { NavShell } from '@/components/NavShell';
import { ContentRenderer } from '@/components/ContentRenderer';
import { ScreenLoader } from '@/components/ScreenLoader';
import { ACList } from '@/components/ACList';
import { QuickCheck } from '@/components/QuickCheck';
import { Button } from '@/components/Button';
import { TicketUpdatedMeta } from '@/components/TicketUpdatedMeta';
import { useProgress } from '@/contexts/ProgressContext';
import { useTicketState } from '@/hooks/useTicketState';
import { useJournal } from '@/hooks/useJournal';
import { ticketById, ticketsBySprint } from '@shared/data/tickets';
import { loadTicketBody } from '@/data/ticketBody';
import { sprintById } from '@shared/data/sprints';
import { HOMEWORK, type Homework } from '@/data/homework';
import { HOMEWORK_V2 } from '@/data/homework-v2';
import { HomeworkV2View } from '@/components/HomeworkV2View';
import { Homework as HomeworkIcon } from '@/icons';
import { formatHomeworkPrompt } from '@/lib/homework-format';
import type { TicketContentRef, TicketMeta } from '@/types';

// ─── Homework adapter ──────────────────────────────────────────────────────
//
// slot_homework_content_engineering · HW.3 · 18 MAY 2026.
//
// /ticket/HW-S07-01 routes through this Ticket screen via the adapter below.
// Homework entries live in `app/src/data/homework.ts` (separate shape from
// Ticket — has `code`, `prompt`, `icon`, `description`). When the ticketId
// has «HW-» prefix we look up by code и wrap the Homework as a TicketMeta-
// shaped object so existing NavShell / Topbar / side panel chrome works
// unchanged. Homework state lives в `progress.homework[hw.id]` (snake_case
// id), not progress.tickets — so render branches read it via useProgress
// directly instead of useTicketState (which is no-op for empty AC anyway).

const homeworkByCode = (code: string): Homework | undefined =>
  HOMEWORK.find((h) => h.code === code);

const homeworkAsTicket = (hw: Homework): TicketMeta => ({
  id: hw.code,
  sprintId: hw.sprintId,
  ord: 0,
  type: 'homework',
  priority: hw.priority,
  title: hw.title,
  subtitle: hw.description,
  estimateMin: hw.estimateMin,
  ac: [],
  istqb: [],
  skills: [],
});

interface TicketScreenProps {
  ticketId: string;
  onLogo: () => void;
  onDashboard: () => void;
  onSprint: (sprintId: string) => void;
  onTicket: (id: string) => void;
  /**
   * RISO Phase C (16 MAY 2026) · optional nav callbacks for NavShell routing.
   * Falls back к onSprint(current sprint) or onDashboard when not provided ·
   * App.tsx wires gradually as Phase D screens (Sprints/Progress) finalize.
   */
  onSprints?: () => void;
  onProgress?: () => void;
  onBuddy?: () => void;
  /** Fallback for nav keys not handled here (practice/portfolio · App dispatcher). */
  onNavFallback?: (key: string) => void;
}

export function TicketScreen({
  ticketId,
  onLogo,
  onDashboard,
  onSprint,
  onTicket,
  onSprints,
  onProgress,
  onBuddy,
  onNavFallback,
}: TicketScreenProps) {
  const { progress, setHomeworkStatus, patchHomework } = useProgress();
  // Homework adapter: HW-* prefix → look up by code → wrap as TicketMeta.
  // Fall through к standard ticketById for bug/lesson/etc.
  const homework = ticketId.startsWith('HW-') ? homeworkByCode(ticketId) : undefined;

  // slot_homework_rework · prefer the v2 entry by id when present (пилот S01-S02);
  // S03+ falls through to the legacy prompt render below (no_content_loss).
  const hwV2 = homework ? HOMEWORK_V2.find((h) => h.id === homework.id) : undefined;
  if (homework && hwV2) {
    return (
      <HomeworkV2View
        hw={hwV2}
        subtitle={homework.description}
        hwProgress={progress.homework?.[hwV2.id]}
        onSetStatus={setHomeworkStatus}
        onPatch={patchHomework}
        onLogo={onLogo}
        onBack={() => onSprint(homework.sprintId)}
      />
    );
  }

  const ticket = homework ? homeworkAsTicket(homework) : ticketById(ticketId);
  if (!ticket) {
    return <NotFound ticketId={ticketId} onLogo={onLogo} onDashboard={onDashboard} />;
  }
  return (
    <TicketView
      ticket={ticket}
      homework={homework}
      onLogo={onLogo}
      onDashboard={onDashboard}
      onSprint={onSprint}
      onTicket={onTicket}
      onSprints={onSprints}
      onProgress={onProgress}
      onBuddy={onBuddy}
      onNavFallback={onNavFallback}
    />
  );
}

function NotFound({
  ticketId,
  onLogo,
  onDashboard,
}: {
  ticketId: string;
  onLogo: () => void;
  onDashboard: () => void;
}) {
  return (
    <NavShell screen="ticket" onNav={() => onDashboard()}>
      <div className="screen">
        <SkipLink />
        <Topbar
          crumb={[
            { label: 'Studio', onClick: onLogo },
            { label: 'Тикет не найден', here: true },
          ]}
          onLogo={onLogo}
        />
        <main id="main-content" className="ticket-main" tabIndex={-1}>
          <div className="ticket-head">
            <h1>Тикет {ticketId} не найден</h1>
            <Button variant="ghost" onClick={onDashboard}>
              ← К Dashboard
            </Button>
          </div>
        </main>
      </div>
    </NavShell>
  );
}

function TicketView({
  ticket,
  homework,
  onLogo,
  onDashboard,
  onSprint,
  onTicket,
  onSprints,
  onProgress,
  onBuddy,
  onNavFallback,
}: {
  ticket: TicketMeta;
  /**
   * Homework adapter handoff · slot_homework_content_engineering HW.3 · 18 MAY 2026.
   * When defined, render branches switch to homework variant: coral pill,
   * full prompt as body, no AC checklist, no Quick check, «Сдать» CTA в side
   * panel instead of «Move to Done». Status read/write goes к progress.homework
   * (via useProgress.setHomeworkStatus), not progress.tickets.
   */
  homework?: Homework;
  onLogo: () => void;
  onDashboard: () => void;
  onSprint: (sprintId: string) => void;
  onTicket: (id: string) => void;
  onSprints?: () => void;
  onProgress?: () => void;
  onBuddy?: () => void;
  onNavFallback?: (key: string) => void;
}) {
  const { progress, setLast, setHomeworkStatus } = useProgress();
  const ts = useTicketState(ticket);
  const journal = useJournal(ticket.id);

  // Lazy-load the heavy `contentRef` body. Sprint metadata renders immediately;
  // body chunk arrives shortly after (per-sprint dynamic import · cached).
  // Homework variant: prompt is already in memory · synthesize body sync.
  const [body, setBody] = useState<TicketContentRef | undefined>(undefined);
  const [bodyLoading, setBodyLoading] = useState(true);
  useEffect(() => {
    if (homework) {
      setBody({ kind: 'inline', html: formatHomeworkPrompt(homework.prompt) });
      setBodyLoading(false);
      return;
    }
    let cancelled = false;
    setBodyLoading(true);
    void loadTicketBody(ticket.id).then((b) => {
      if (cancelled) return;
      setBody(b);
      setBodyLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [ticket.id, homework]);

  useEffect(() => {
    setLast(ticket.sprintId, ticket.id);
  }, [ticket.sprintId, ticket.id, setLast]);

  const sprint = sprintById(ticket.sprintId);
  // Prev/next navigation · for bug/lesson use ticketsBySprint; for homework
  // use HOMEWORK filtered by sprint (homework lives in separate data file).
  const sprintTickets = homework
    ? HOMEWORK.filter((h) => h.sprintId === ticket.sprintId).map((h) => ({ id: h.code }))
    : ticketsBySprint(ticket.sprintId);
  const idx = sprintTickets.findIndex((t) => t.id === ticket.id);
  const prevTicketId = idx > 0 ? sprintTickets[idx - 1]?.id : undefined;
  const nextTicketId = idx < sprintTickets.length - 1 ? sprintTickets[idx + 1]?.id : undefined;

  // slot_ticket_long_content_ux · TLU.4 · 31 MAY 2026 · Genie revert (clarified).
  // 17 MAY removal of QC от gate (slot_riso_phase_h_final_smoke H.4-EXT4.2) was
  // «quick check informational». Genie revert 31 MAY: «если в тикете есть quick
  // check, без него нельзя подвинуть в done · не нужен правильный ответ · главное
  // что надо нажать» = QC engagement required (any answer · correct OR wrong),
  // не only correct. Gate: AC done AND (no QC OR QC answered).
  // `qcAnswered = !!tp.quickCheck.state` covers оба варианта (state ∈ 'correct' | 'wrong').
  const closed = ts.status === 'done';
  const tp = progress.tickets[ticket.id];
  const qcAnswered = !!tp?.quickCheck?.state;
  const allDone = ts.acAllDone && (!ticket.quickCheck || qcAnswered);
  const remaining = ts.acTotal - ts.acDone;
  const qcPending = !!ticket.quickCheck && !qcAnswered;
  // CTA disabled-state label · reused in side close-btn + mobile action bar.
  // remaining>0 + QC pending: "Осталось: N + Quick check"
  // remaining=0 + QC pending: "Осталось: Quick check"
  // remaining>0 + no QC:      "Осталось: N"
  // slot_t00_ftue_rewrite (6 JUN 2026) · chrome russification per external
  // tester round: russian-first labels, original term as secondary. «Проверка
  // себя» = the QC block's on-screen name (qc.prompt in ticket data).
  const galki = (n: number): string => {
    const word =
      n % 10 === 1 && n % 100 !== 11
        ? 'галочка'
        : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20)
          ? 'галочки'
          : 'галочек';
    return `${n} ${word}`;
  };
  const pendingLabel =
    qcPending && remaining === 0
      ? 'Осталось: «Проверка себя»'
      : qcPending
        ? `Осталось: ${galki(remaining)} + «Проверка себя»`
        : `Осталось: ${galki(remaining)}`;

  // Homework status path · separate from progress.tickets per HW.3 design.
  // Status flow: locked → in-progress (auto on first open) → done (via «Сдать»).
  const homeworkStatus = homework ? (progress.homework?.[homework.id]?.status ?? 'locked') : undefined;
  const homeworkDone = homeworkStatus === 'done';

  // Auto-advance locked → in-progress when student opens homework first time.
  useEffect(() => {
    if (homework && homeworkStatus === 'locked') {
      setHomeworkStatus(homework.id, 'in-progress');
    }
  }, [homework, homeworkStatus, setHomeworkStatus]);

  // (14 AUG) Reverted the auto «open → in-progress»: merely opening/peeking a
  // ticket no longer marks it «в работе» (it polluted the plan / «Сегодня в
  // очереди» with peeked-ahead tickets). Instead the student explicitly clicks
  // «Взять в работу» below — like a real tracker's «start progress». in-progress
  // still also happens automatically on the first AC tick / quick-check
  // (useTicketState) and manual board drag.
  const handleClose = () => {
    if (allDone && !closed) ts.setStatus('done');
  };

  const handleHomeworkSubmit = () => {
    if (homework && !homeworkDone) {
      setHomeworkStatus(homework.id, 'done');
    }
  };

  // RISO Phase C · NavShell click routing · pass-through к existing callbacks.
  // Unwired keys fall back к onSprint (current sprint) or onDashboard.
  const handleNav = useCallback(
    (key: string) => {
      switch (key) {
        case 'dashboard':
          onDashboard();
          break;
        case 'sprint':
          onSprint(ticket.sprintId);
          break;
        case 'sprints':
          (onSprints ?? onDashboard)();
          break;
        case 'progress':
          (onProgress ?? onDashboard)();
          break;
        // SBM merge (18 MAY 2026): 'board' case retired · Sidebar NAV_ITEMS no
        // longer emits 'board' key ('sprint' renamed к Доска · same routing).
        case 'buddy':
          if (onBuddy) onBuddy();
          break;
        case 'atelier':
          // Handled internally by NavShell (overlay toggle).
          break;
        default:
          onNavFallback?.(key);
          break;
      }
    },
    [onDashboard, onSprint, ticket.sprintId, onSprints, onProgress, onBuddy, onNavFallback],
  );

  return (
    <NavShell screen="ticket" onNav={handleNav}>
      <div className="screen">
        <SkipLink />
      <Topbar
        crumb={[
          { label: 'Studio', onClick: onLogo },
          { label: 'Dashboard', onClick: onDashboard },
          { label: sprint?.id ?? ticket.sprintId, onClick: () => onSprint(ticket.sprintId) },
          { label: ticket.id, here: true },
        ]}
        onLogo={onLogo}
      />
      <main
        id="main-content"
        className={`ticket-main${ticket.type === 'bug' ? ' ticket-main--bug' : ''}`}
        tabIndex={-1}
      >
        <div className="ticket-head">
          <div className="id-row">
            <span className="id">{ticket.id}</span>
            {homework ? (
              <span className="pill pill--homework" data-testid="homework-pill">
                <HomeworkIcon
                  width={14}
                  height={14}
                  style={{ display: 'inline-block', verticalAlign: '-2px', marginRight: 4 }}
                  aria-hidden="true"
                />
                ДОМАШКА
              </span>
            ) : (
              <span className="pill docs">
                {ticket.type === 'bug'
                  ? '🐛 BUG'
                  : ticket.type === 'docs'
                    ? 'УРОК'
                    : ticket.type.toUpperCase()}
              </span>
            )}
            {ticket.type === 'master' && <span className="pill master">мастер-тема</span>}
            {ticket.type === 'bug' ? (
              <SeverityBadge priority={ticket.priority} />
            ) : (
              <span className={`pill ${ticket.priority === 'P0' ? 'p0-pill' : 'p1-pill'}`}>
                {ticket.priority}
              </span>
            )}
            <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              {/* Q-6 (CHAT_2B Genie adopt 16 MAY · component @ 7e8cdc0):
                  «обновлён N мин назад» relative time meta · null-render если
                  no updatedAt yet (legacy data · brand-new tickets). */}
              <TicketUpdatedMeta ticket={tp} />
              <span>~{ticket.estimateMin}м · автор: Buddy</span>
            </span>
          </div>
          <h1>{ticket.title}</h1>
          {ticket.subtitle && (
            <p className="lead" style={{ marginBottom: 12 }}>
              {ticket.subtitle}
            </p>
          )}
        </div>

        <div>
          <div className="body-card">
            {/* slot_t00_ftue_rewrite (7 JUN 2026 · Genie): дефолтные эмодзи-иконки
                зачищены из хрома тикета (per `feedback_qa_studio_no_emoji_no_ai_patterns`). */}
            <h2>
              {homework
                ? 'О чём эта домашка'
                : `О чём этот ${ticket.type === 'docs' ? 'урок' : 'тикет'}`}
            </h2>
            {bodyLoading && (
              <ScreenLoader label={homework ? 'Загружаю задание…' : 'Загружаю урок…'} />
            )}
            {!bodyLoading && body?.kind === 'inline' && (
              <ContentRenderer html={body.html} ticketId={ticket.id} />
            )}
            {!bodyLoading && body?.kind === 'markdown' && (
              <p style={{ whiteSpace: 'pre-wrap' }}>{body.body}</p>
            )}

            {!homework && (
              <div className="section">
                <h2>
                  Условия готовности · Acceptance criteria
                </h2>
                <ACList items={ticket.ac} checked={ts.acChecked} onToggle={ts.toggleAC} />
              </div>
            )}

            {!homework && ticket.quickCheck && (
              <div className="section">
                <QuickCheck ticketId={ticket.id} qc={ticket.quickCheck} />
              </div>
            )}

            <div className="section">
              <h2>
                Журнал · твои заметки
              </h2>
              <div className="add-comment">
                <textarea
                  value={journal.note}
                  onChange={(e) => journal.setNote(e.target.value)}
                  placeholder="Запиши, что было ясно/неясно, какие вопросы появились…"
                  aria-label="Журнал тикета"
                />
              </div>
              {journal.note && (
                <div
                  style={{
                    fontFamily: 'Space Mono',
                    fontSize: 10,
                    color: 'var(--ink-mute)',
                    marginTop: 6,
                  }}
                >
                  ✓ Сохраняется автоматически
                </div>
              )}
            </div>

            {ticket.type === 'bug' && ticket.mission?.gwt && (
              <div className="section">
                <h2>
                  Шаги воспроизведения
                </h2>
                <BugGwtBlock gwt={ticket.mission.gwt} />
              </div>
            )}

            {ticket.type === 'bug' && ticket.mission?.sample && (
              <div className="section">
                <h2>
                  Что ожидалось vs Что произошло
                </h2>
                <BugExpectedActualBlock raw={ticket.mission.sample} />
              </div>
            )}

            {ticket.mission && ticket.type !== 'bug' && (
              <div className="section">
                <div className="practice-inline">
                  <div className="h">Mission</div>
                  <div className="desc">{ticket.mission.cta}</div>
                  {ticket.mission.url && (
                    <div className="submit">
                      <input readOnly value={ticket.mission.url} />
                      <a
                        className="btn-primary"
                        href={ticket.mission.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ textDecoration: 'none' }}
                      >
                        Открыть →
                      </a>
                    </div>
                  )}
                  <div style={{ fontSize: 12, color: 'var(--ink-soft)', marginTop: 8 }}>
                    Acceptance: {ticket.mission.acceptanceText}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="side">
          {!homework && allDone && !closed && (
            <div className="ready-banner">
              <div className="rb-mark">✓</div>
              <div className="rb-text">
                <strong>Все условия выполнены</strong>
                <span>
                  Галочки отмечены{ticket.quickCheck ? ', «Проверка себя» пройдена' : ''}. Можно закрывать.
                </span>
              </div>
            </div>
          )}
          {!homework && closed && (
            <div className="closed-banner">
              <div className="cb-mark">✓</div>
              <div className="cb-text">
                <strong>Тикет закрыт</strong>
                <span>Buddy записал в твой прогресс · +1 ★</span>
              </div>
            </div>
          )}
          {homework && homeworkDone && (
            <div className="closed-banner">
              <div className="cb-mark">✓</div>
              <div className="cb-text">
                <strong>Домашка сдана</strong>
                <span>Можно вернуться и дополнить журнал · статус уже final.</span>
              </div>
            </div>
          )}
          {homework ? (
            <button
              type="button"
              className={`close-btn ${homeworkDone ? 'is-closed' : 'is-ready'}`}
              disabled={homeworkDone}
              onClick={handleHomeworkSubmit}
              data-testid="homework-submit"
            >
              {homeworkDone ? '✓ Сдано' : '✓ Сдать'}
            </button>
          ) : ts.status === 'todo' ? (
            // Explicit «start progress»: opening no longer auto-marks in-progress,
            // so the student takes the ticket into work deliberately (reflected on
            // «Сегодня» + the board). Then the close flow below takes over.
            <button
              type="button"
              className="close-btn is-ready"
              onClick={() => ts.setStatus('in-progress')}
              data-testid="take-in-progress"
            >
              Взять в работу
            </button>
          ) : (
            <button
              type="button"
              className={`close-btn ${closed ? 'is-closed' : ''} ${allDone && !closed ? 'is-ready' : ''}`}
              disabled={!allDone || closed}
              onClick={handleClose}
              data-tour="ticket-close"
            >
              {closed
                ? '✓ Тикет закрыт'
                : allDone
                  ? '✓ Перенести в «Сделано»'
                  : pendingLabel}
            </button>
          )}

          <div className="side-card">
            <div className="h">Properties</div>
            <div className="side-row">
              <span>Status</span>
              <span
                className={`v ${
                  homework
                    ? homeworkDone
                      ? 'green'
                      : 'red'
                    : closed
                      ? 'green'
                      : ts.status === 'in-progress'
                        ? 'red'
                        : ''
                }`}
              >
                {homework
                  ? homeworkDone
                    ? 'Сдано'
                    : 'В работе'
                  : closed
                    ? 'Закрыт'
                    : ts.status === 'in-progress'
                      ? 'В работе'
                      : 'К работе'}
              </span>
            </div>
            <div className="side-row">
              <span>Priority</span>
              <span className="v">{ticket.priority}</span>
            </div>
            <div className="side-row">
              <span>Type</span>
              <span className="v">
                {homework
                  ? 'Домашка'
                  : ticket.type === 'docs'
                    ? 'Урок'
                    : ticket.type === 'master'
                      ? '★ Master'
                      : ticket.type}
              </span>
            </div>
            <div className="side-row">
              <span>Estimate</span>
              <span className="v">{ticket.estimateMin} мин</span>
            </div>
            <div className="side-row">
              <span>Sprint</span>
              <span className="v">
                {sprint?.id} · {sprint?.category}
              </span>
            </div>
          </div>

          {!homework && (
            <div className="side-card">
              <div className="h">AC progress</div>
              <div className="side-row">
                <span>Done</span>
                <span className="v green">
                  {ts.acDone} / {ts.acTotal}
                </span>
              </div>
              <div className="ac-mini-bar">
                <div style={{ width: `${ts.acPct}%` }} />
              </div>
            </div>
          )}

          <div className="side-card">
            <div className="h">ISTQB</div>
            <div className="body-text">
              {ticket.istqb.length === 0 ? 'Не привязан к CTFL' : ticket.istqb.join(' · ')}
            </div>
          </div>

          {(prevTicketId || nextTicketId) && (
            // `side-card--nav` · hidden ≤1099px (the floating action-bar carries
            // prev/next there) · kept on desktop where there is no floating bar.
            <div className="side-card side-card--nav">
              <div className="h">↔ Навигация</div>
              <div className="side-row">
                {prevTicketId ? (
                  <button
                    type="button"
                    onClick={() => onTicket(prevTicketId)}
                    className="ticket-nav-link ticket-nav-link--prev"
                  >
                    ← {prevTicketId}
                  </button>
                ) : (
                  <span className="ticket-nav-empty">—</span>
                )}
                {nextTicketId ? (
                  <button
                    type="button"
                    onClick={() => onTicket(nextTicketId)}
                    className="ticket-nav-link ticket-nav-link--next"
                  >
                    {nextTicketId} →
                  </button>
                ) : (
                  <span className="ticket-nav-empty ticket-nav-empty--right">—</span>
                )}
              </div>
            </div>
          )}

          {tp?.completedAt && (
            <div className="side-card">
              <div className="h">✓ Закрыт</div>
              <div className="body-text">{new Date(tp.completedAt).toLocaleString('ru-RU')}</div>
            </div>
          )}
        </div>
        </main>
        {/* slot_ticket_long_content_ux · TLU.3 · 31 MAY 2026 · Variant A pick.
            Mobile-only floating action bar (display:none on desktop · CSS-gated).
            Mirrors close-btn state machine так на длинных MS-XX уроках / HW-XX
            заданиях / bug-репортах close-CTA + prev/next nav достижимы без
            скролла назад. Desktop reach problem solved by `.side` sticky rule
            in globals.css (same slot). All 3 variants (bug/lesson/homework)
            handled · `.side` stack preserved (Properties/AC/ISTQB visible after
            scroll · no_content_loss). */}
        <TicketActionBar
          homework={homework}
          homeworkDone={homeworkDone}
          handleHomeworkSubmit={handleHomeworkSubmit}
          closed={closed}
          allDone={allDone}
          pendingLabel={pendingLabel}
          handleClose={handleClose}
          isTodo={!homework && ts.status === 'todo'}
          onTakeInProgress={() => ts.setStatus('in-progress')}
          prevTicketId={prevTicketId}
          nextTicketId={nextTicketId}
          onTicket={onTicket}
        />
      </div>
    </NavShell>
  );
}

/**
 * Mobile floating action bar · slot_ticket_long_content_ux TLU.3 · 31 MAY 2026.
 *
 * Renders below content на mobile (<1100px) so close-CTA + prev/next reachable
 * без скролла к bottom-of-page `.side` stack. Desktop (≥1100px) hides via
 * `.ticket-action-bar { display: none }` default · media-query enables. Bar
 * sits на `bottom: 56px` (above existing BottomTabs which is `bottom: 0`
 * ~50px tall · z-index 35) с `z-index: 36`.
 *
 * CTA mirrors `close-btn` state machine exactly:
 *   bug/lesson: "Осталось: N" (disabled) → "✓ Move to Done" (ready) → "✓ Тикет закрыт" (done)
 *   homework:   "✓ Сдать" (in-progress) → "✓ Сдано" (done)
 *
 * Test anchor: `data-testid="ticket-action-bar"`.
 */
function TicketActionBar({
  homework,
  homeworkDone,
  handleHomeworkSubmit,
  closed,
  allDone,
  pendingLabel,
  handleClose,
  isTodo,
  onTakeInProgress,
  prevTicketId,
  nextTicketId,
  onTicket,
}: {
  homework?: Homework;
  homeworkDone: boolean;
  handleHomeworkSubmit: () => void;
  closed: boolean;
  allDone: boolean;
  pendingLabel: string;
  handleClose: () => void;
  isTodo?: boolean;
  onTakeInProgress: () => void;
  prevTicketId?: string;
  nextTicketId?: string;
  onTicket: (id: string) => void;
}) {
  const ctaLabel = homework
    ? homeworkDone
      ? '✓ Сдано'
      : '✓ Сдать'
    : isTodo
      ? 'Взять в работу'
      : closed
        ? '✓ Тикет закрыт'
        : allDone
          ? '✓ Перенести в «Сделано»'
          : pendingLabel;
  const ctaDisabled = homework ? homeworkDone : isTodo ? false : !allDone || closed;
  const ctaState = homework
    ? homeworkDone
      ? 'is-closed'
      : 'is-ready'
    : isTodo
      ? 'is-ready'
      : closed
        ? 'is-closed'
        : allDone
          ? 'is-ready'
          : '';
  const ctaHandler = homework ? handleHomeworkSubmit : isTodo ? onTakeInProgress : handleClose;
  // Portal to <body> so the fixed bar anchors to the viewport, not to `.screen`
  // — `.screen` runs the `screenIn` transform animation, and a transformed
  // ancestor turns `position:fixed` into "fixed-to-ancestor", which pinned the
  // bar to the bottom of the tall scroll instead of the screen edge (a cream
  // gap appeared above BottomTabs · «кусок под навигацией», 6 JUN 2026). Same
  // PortalToBody reason as PomodoroWidget. Props/handlers ride the React tree.
  if (typeof document === 'undefined') return null;
  return createPortal(
    <div
      className="ticket-action-bar"
      data-testid="ticket-action-bar"
      role="toolbar"
      aria-label="Действия с тикетом"
    >
      <button
        type="button"
        className="ticket-action-bar__nav"
        disabled={!prevTicketId}
        onClick={() => prevTicketId && onTicket(prevTicketId)}
        aria-label="Предыдущий тикет"
        data-testid="ticket-action-bar-prev"
      >
        ←
      </button>
      <button
        type="button"
        className={`ticket-action-bar__cta ${ctaState}`}
        disabled={ctaDisabled}
        onClick={ctaHandler}
        data-testid="ticket-action-bar-cta"
      >
        {ctaLabel}
      </button>
      <button
        type="button"
        className="ticket-action-bar__nav"
        disabled={!nextTicketId}
        onClick={() => nextTicketId && onTicket(nextTicketId)}
        aria-label="Следующий тикет"
        data-testid="ticket-action-bar-next"
      >
        →
      </button>
    </div>,
    document.body,
  );
}

/**
 * Severity badge for bug-variant tickets · per Stage C §2.5 B3=C border+badge.
 * Maps priority → semantic severity word + pill color (P0 critical · P1 high ·
 * P2 medium · P3 low).
 */
export function SeverityBadge({ priority }: { priority: TicketMeta['priority'] }) {
  const meta = {
    P0: { label: 'critical', cls: 'severity-badge severity-badge--p0' },
    P1: { label: 'high', cls: 'severity-badge severity-badge--p1' },
    P2: { label: 'medium', cls: 'severity-badge severity-badge--p2' },
    P3: { label: 'low', cls: 'severity-badge severity-badge--p3' },
  }[priority];
  return (
    <span className={meta.cls} aria-label={`Severity: ${priority} ${meta.label}`}>
      {priority} {meta.label}
    </span>
  );
}

/**
 * GWT block · per Stage C §2.5 B4=B Given/When/Then. Three sections inside a
 * `--panel` card · `.h3-ui` labels · multi-line `then`/`when` accepted as-is
 * (`white-space: pre-line`).
 */
export function BugGwtBlock({ gwt }: { gwt: NonNullable<NonNullable<TicketMeta['mission']>['gwt']> }) {
  const rows = [
    { label: 'Дано (Given)', value: gwt.given },
    { label: 'Когда (When)', value: gwt.when },
    { label: 'Тогда (Then)', value: gwt.then },
  ];
  return (
    <div className="bug-gwt" data-testid="bug-gwt">
      {rows.map((row) => (
        <div key={row.label} className="bug-gwt__row">
          <div className="bug-gwt__label">{row.label}</div>
          <div className="bug-gwt__value">{row.value}</div>
        </div>
      ))}
    </div>
  );
}

/**
 * Expected vs Actual block · per Stage C §2.5 B5=A desktop side-by-side / B
 * mobile stacked. Phase 3 reads prose from `ticket.mission.sample` and splits
 * on `\n---\n` («expected — actual»). Phase 3.4 content authoring may extend
 * to a structured field (per ASK-M6-1 logged in slot · pending Genie).
 */
export function BugExpectedActualBlock({ raw }: { raw: string }) {
  const SEPARATOR = /\n---\n/;
  const parts = raw.split(SEPARATOR, 2);
  const expected = parts[0]?.trim() ?? '';
  const actual = parts[1]?.trim() ?? '';
  return (
    <div className="bug-ea" data-testid="bug-expected-actual">
      <div className="bug-ea__col">
        <div className="bug-ea__head">Ожидалось</div>
        <div className="bug-ea__body">{expected}</div>
      </div>
      {actual && (
        <div className="bug-ea__col">
          <div className="bug-ea__head">Произошло</div>
          <div className="bug-ea__body">{actual}</div>
        </div>
      )}
    </div>
  );
}
